/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Run a saved report, chart it, export it.
 *
 * The filter bar narrows without editing the report, which is what makes one
 * saved report serve "this quarter" and "last quarter" without cloning it. The
 * overrides go to the server as a narrowing-only addition — the viewer cannot
 * widen a report past what its definition and the reader's own permissions
 * already allow.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Menu, MenuItem, Snackbar, Tab, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddchartRounded, ArrowBackRounded, BarChartRounded, ContentCopyRounded,
  DeleteOutlineRounded, DownloadRounded, EditOutlined, GridOnRounded,
  RefreshRounded, ScheduleSendRounded, ShareOutlined, TableChartOutlined,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useCapability } from 'auth/usePermission';
import { useCurrency } from 'context/CurrencyContext';
import colors from 'styles/tokens/colors';
import ReportChart from './ReportChart';
import ReportMatrix from './ReportMatrix';
import ReportTable from './ReportTable';
import ScheduleDialog from './ScheduleDialog';
import ShareDialog from './ShareDialog';

const PERIODS = [
  { value: '', label: 'All time' },
  { value: 'TODAY', label: 'Today' },
  { value: 'THIS_WEEK', label: 'This week' },
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LAST_MONTH', label: 'Last month' },
  { value: 'THIS_QUARTER', label: 'This quarter' },
  { value: 'LAST_QUARTER', label: 'Last quarter' },
  { value: 'THIS_YEAR', label: 'This year' },
  { value: 'LAST_N_DAYS:30', label: 'Last 30 days' },
  { value: 'LAST_N_DAYS:90', label: 'Last 90 days' },
];

const CHART_TYPES = [
  'bar', 'horizontal_bar', 'line', 'area', 'donut', 'pie', 'funnel',
];

export default function ReportViewer() {
  const { reportId } = useParams();
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const canExport = useCapability('EXPORT');
  // Safe to call unconditionally: CurrencyProvider wraps every protected
  // route (App.js), and these pages are all protected.
  const { currency } = useCurrency();

  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState('');
  const [dateField, setDateField] = useState('');
  const [dateFields, setDateFields] = useState([]);
  const [fields, setFields] = useState([]);
  const [exportMenu, setExportMenu] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [chartDraft, setChartDraft] = useState(null);
  const [drill, setDrill] = useState(null);
  const [toast, setToast] = useState(null);

  /* Narrowing overrides sent with the run. Empty when no period is chosen, so
     a report with no date filter is unaffected by the bar being present. */
  const overrides = useMemo(() => {
    if (!period || !dateField) return null;
    return { conditions: [{ field: dateField, operator: 'relative_range', value: period }] };
  }, [period, dateField]);

  const loadReport = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/analytics/reports/${reportId}`);
      setReport(data);
      setError(null);
      const catalogFields = await axiosInstance
        .get(`/analytics/catalog/objects/${data.primary_object}/fields`)
        .then((r) => r.data).catch(() => []);
      setFields(Array.isArray(catalogFields) ? catalogFields : []);
      const dates = (Array.isArray(catalogFields) ? catalogFields : [])
        .filter((f) => (f.type === 'date' || f.type === 'datetime') && !f.api_name.includes('.'));
      setDateFields(dates);
      // Default the filter bar to the field the report already filters on, so
      // changing the period narrows the same axis rather than a second one.
      const existing = data.definition?.filters?.conditions
        ?.find((c) => dates.some((d) => d.api_name === c.field));
      setDateField(existing?.field ?? dates.find((d) => d.api_name === 'created_at')?.api_name
                   ?? dates[0]?.api_name ?? '');
    } catch (err) {
      setError(err?.response?.status === 404
        ? 'This report does not exist, or it has not been shared with you.'
        : 'That report could not be opened.');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, reportId]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const run = useCallback(async () => {
    if (!report) return;
    setRunning(true);
    try {
      const { data } = await axiosInstance.post(
        `/analytics/reports/${reportId}/run`, { overrides },
      );
      setResult(data);
      setError(null);
    } catch (err) {
      setResult(null);
      setError(readError(err, 'This report could not be run.'));
    } finally {
      setRunning(false);
    }
  }, [axiosInstance, reportId, report, overrides]);

  useEffect(() => { if (report) run(); }, [report, run]);

  /**
   * Show the records behind a grouped row.
   *
   * Built by re-running the report as a tabular query with the row's own
   * group values pinned as extra filters, rather than by keeping the source
   * rows around. Keeping them would mean fetching every underlying record to
   * render a summary — the opposite of why the report is grouped.
   *
   * Date buckets are turned back into a range: a month bucket of 2026-09-01
   * means the whole of September, not midnight on the 1st.
   */
  const drillDown = async (row) => {
    const groupings = report?.definition?.group_by ?? [];
    if (!groupings.length) return;

    const conditions = [];
    groupings.forEach((grouping, index) => {
      const key = `${grouping.field}${grouping.bucket ? `__${grouping.bucket}` : ''}`
        .replace(/\./g, '__');
      const value = row[key];
      const id = 900 + index;   // out of the way of the report's own ids

      if (value === null || value === undefined || value === '') {
        conditions.push({ id, field: grouping.field, operator: 'is_empty' });
        return;
      }
      if (grouping.bucket) {
        const start = new Date(value);
        const end = new Date(start);
        const step = { day: 1, week: 7 }[grouping.bucket];
        if (step) end.setDate(end.getDate() + step - 1);
        else if (grouping.bucket === 'month') { end.setMonth(end.getMonth() + 1); end.setDate(0); }
        else if (grouping.bucket === 'quarter') { end.setMonth(end.getMonth() + 3); end.setDate(0); }
        else { end.setFullYear(end.getFullYear() + 1); end.setDate(end.getDate() - 1); }
        conditions.push({
          id, field: grouping.field, operator: 'between',
          value: [iso(start), iso(end)],
        });
        return;
      }
      conditions.push({ id, field: grouping.field, operator: 'equals', value });
    });

    const label = groupings
      .map((g) => {
        const key = `${g.field}${g.bucket ? `__${g.bucket}` : ''}`.replace(/\./g, '__');
        const column = result?.columns?.find((c) => c.key === key);
        return formatCell(row[key], column);
      })
      .filter(Boolean).join(' · ');

    // The summary row's count measure, when present — shown in the dialog so
    // the list is trusted against the number that was clicked, not against
    // how many rows fit on screen before scrolling.
    const countColumn = result?.columns?.find(
      (c) => c.is_aggregate && String(c.key).startsWith('count__'),
    );
    const expectedCount = countColumn != null && row[countColumn.key] != null
      ? Number(row[countColumn.key])
      : null;

    setDrill({
      label, expectedCount, loading: true, result: null, error: null,
    });

    const existing = report.definition?.filters?.conditions ?? [];
    // Fold the viewer's period bar into the drill filters. The ad-hoc run
    // endpoint has no overrides field; without this, "This month" on the
    // summary and "All time" in the drill list disagree.
    const overrideConditions = (overrides?.conditions ?? []).map((condition, index) => ({
      ...condition,
      id: 800 + index,
    }));
    const definition = {
      ...report.definition,
      primary_object: report.primary_object,
      // Drop the grouping entirely: this is the record list, not a summary.
      group_by: undefined,
      aggregates: undefined,
      columns: drillColumns(fields, dateFields, report),
      filters: {
        // AND, always: the row's group values narrow whatever the report
        // already filtered. Reusing the saved report's own filter logic
        // expression would renumber into nonsense.
        logic: 'AND',
        conditions: [...existing, ...overrideConditions, ...conditions],
      },
      sort: [],
      limit: 200,
    };

    try {
      const { data } = await axiosInstance.post('/analytics/reports/run', {
        definition, limit: 200,
      });
      setDrill((d) => ({ ...d, loading: false, result: data }));
    } catch (err) {
      setDrill((d) => ({
        ...d, loading: false,
        error: readError(err, 'Those records could not be loaded.'),
      }));
    }
  };

  const exportAs = async (format) => {
    setExportMenu(null);
    try {
      const response = await axiosInstance.get(
        `/analytics/reports/${reportId}/export`,
        { params: { format }, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filenameFrom(response, report.name, format);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // An error response to a blob request arrives as a Blob, so the usual
      // detail-reading path finds nothing useful.
      const message = await readBlobError(err);
      setToast({ severity: 'error', message: message ?? 'The export failed.' });
    }
  };

  const saveChart = async () => {
    if (!chartDraft?.name?.trim()) return;
    try {
      const payload = {
        name: chartDraft.name.trim(),
        chart_type: chartDraft.chart_type,
        config: {
          category_key: chartDraft.category_key,
          value_key: chartDraft.value_key,
        },
        order_index: report.charts?.length ?? 0,
      };
      if (chartDraft.id) {
        await axiosInstance.patch(
          `/analytics/reports/${reportId}/charts/${chartDraft.id}`, payload);
      } else {
        await axiosInstance.post(`/analytics/reports/${reportId}/charts`, payload);
      }
      setChartDraft(null);
      setToast({ severity: 'success', message: 'Chart saved. You can add it to a dashboard.' });
      await loadReport();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That chart could not be saved.') });
    }
  };

  const deleteChart = async (chartId) => {
    try {
      await axiosInstance.delete(`/analytics/reports/${reportId}/charts/${chartId}`);
      await loadReport();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That chart could not be removed.') });
    }
  };

  const clone = async () => {
    try {
      const { data } = await axiosInstance.post(`/analytics/reports/${reportId}/clone`, {});
      navigate(`/reports/${data.id}/edit`);
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That report could not be copied.') });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">{error ?? 'That report could not be opened.'}</Alert>
          <MDButton variant="text" color="dark" sx={{ mt: 2 }} onClick={() => navigate('/reports')}>
            Back to reports
          </MDButton>
        </MDBox>
      </DashboardLayout>
    );
  }

  const canEdit = report.access === 'edit' || report.access === 'manage';
  const charts = report.charts ?? [];
  // Two dimensions and a measure is the whole requirement for a cross-tab.
  const canPivot = Boolean(
    result?.is_grouped
    && (result.columns ?? []).filter((c) => !c.is_aggregate).length >= 2
    && (result.columns ?? []).some((c) => c.is_aggregate),
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* ------------------------------------------------------ header */}
        <Box display="flex" alignItems="flex-start" gap={1.5} mb={2} flexWrap="wrap">
          <IconButton size="small" onClick={() => navigate('/reports')} sx={{ mt: 0.5 }}>
            <ArrowBackRounded fontSize="small" />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h4" fontWeight={700} lineHeight={1.2} noWrap>
                {report.name}
              </Typography>
              {report.is_template && (
                <Chip size="small" label="Built-in" sx={{
                  backgroundColor: colors.status.info.background,
                  color: colors.status.info.text, fontWeight: 700,
                }} />
              )}
            </Box>
            {report.description && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {report.description}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="Re-run">
              <IconButton size="small" onClick={run} disabled={running}>
                {running ? <CircularProgress size={16} /> : <RefreshRounded fontSize="small" />}
              </IconButton>
            </Tooltip>
            {canExport && (
              <MDButton variant="outlined" color="dark" size="small"
                        onClick={(e) => setExportMenu(e.currentTarget)}>
                <DownloadRounded sx={{ mr: 0.5, fontSize: 18 }} /> Export
              </MDButton>
            )}
            {canExport && (
              <MDButton variant="outlined" color="dark" size="small"
                        onClick={() => setScheduling(true)}>
                <ScheduleSendRounded sx={{ mr: 0.5, fontSize: 18 }} /> Schedule
              </MDButton>
            )}
            <MDButton variant="outlined" color="dark" size="small" onClick={clone}>
              <ContentCopyRounded sx={{ mr: 0.5, fontSize: 18 }} /> Copy
            </MDButton>
            {report.access === 'manage' && (
              <MDButton variant="outlined" color="dark" size="small"
                        onClick={() => setSharing(true)}>
                <ShareOutlined sx={{ mr: 0.5, fontSize: 18 }} /> Share
              </MDButton>
            )}
            {canEdit && !report.is_template && (
              <MDButton variant="gradient" color="info" size="small"
                        onClick={() => navigate(`/reports/${reportId}/edit`)}>
                <EditOutlined sx={{ mr: 0.5, fontSize: 18 }} /> Edit
              </MDButton>
            )}
          </Box>
        </Box>

        {/* -------------------------------------------------- filter bar */}
        {dateFields.length > 0 && (
          <Box display="flex" gap={1} alignItems="center" mb={2} flexWrap="wrap">
            <TextField
              select size="small" label="Period" value={period}
              onChange={(e) => setPeriod(e.target.value)} sx={{ width: 170 }}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
            {period && (
              <TextField
                select size="small" label="Based on" value={dateField}
                onChange={(e) => setDateField(e.target.value)} sx={{ width: 200 }}
              >
                {dateFields.map((f) => (
                  <MenuItem key={f.api_name} value={f.api_name}>{f.label}</MenuItem>
                ))}
              </TextField>
            )}
            {period && (
              <Typography variant="caption" color="text.secondary">
                Narrows the saved report without changing it.
              </Typography>
            )}
          </Box>
        )}

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ------------------------------------------------------- body */}
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px',
                   backgroundColor: colors.background.white, overflow: 'hidden' }}>
          <Box display="flex" alignItems="center" sx={{ px: 1, borderBottom: '1px solid',
                                                        borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab icon={<TableChartOutlined sx={{ fontSize: 18 }} />} iconPosition="start"
                   label="Table" sx={{ minHeight: 48 }} />
              <Tab icon={<BarChartRounded sx={{ fontSize: 18 }} />} iconPosition="start"
                   label={`Charts${charts.length ? ` (${charts.length})` : ''}`}
                   sx={{ minHeight: 48 }} />
              {/* Only when there is something to pivot. A matrix tab on a
                  single-grouping report is a tab that can only apologise. */}
              {canPivot && (
                <Tab icon={<GridOnRounded sx={{ fontSize: 18 }} />} iconPosition="start"
                     label="Matrix" sx={{ minHeight: 48 }} />
              )}
            </Tabs>
            <Box flex={1} />
            {tab === 1 && canEdit && result?.is_grouped && (
              <MDButton
                variant="text" color="info" size="small"
                onClick={() => setChartDraft(newChartDraft(result, charts.length))}
              >
                <AddchartRounded sx={{ mr: 0.5, fontSize: 18 }} /> Add chart
              </MDButton>
            )}
          </Box>

          {tab === 0 && (
            running && !result
              ? <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
              : (
                <ReportTable
                  result={result} currency={currency}
                  onDrillDown={drillDown}
                />
              )
          )}

          {tab === 2 && canPivot && (
            <ReportMatrix result={result} currency={currency} maxHeight={560} />
          )}

          {tab === 1 && (
            <Box p={2}>
              {!result?.is_grouped ? (
                <Alert severity="info">
                  Charts need a grouped report. Open Edit, group by a field and
                  add a summary — then you can chart it and put it on a dashboard.
                </Alert>
              ) : !charts.length ? (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <BarChartRounded sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>No charts yet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    A chart built here can be dropped onto any dashboard.
                  </Typography>
                  {canEdit && (
                    <MDButton variant="gradient" color="info"
                              onClick={() => setChartDraft(newChartDraft(result, 0))}>
                      <AddchartRounded sx={{ mr: 0.75 }} /> Add a chart
                    </MDButton>
                  )}
                </Box>
              ) : (
                <Box display="grid" gap={2}
                     gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }}>
                  {charts.map((chart) => (
                    <Box key={chart.id} sx={{ border: '1px solid', borderColor: 'divider',
                                              borderRadius: '12px', p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" fontWeight={700} flex={1} noWrap>
                          {chart.name}
                        </Typography>
                        {canEdit && (
                          <>
                            <Tooltip title="Edit chart">
                              <IconButton size="small" onClick={() => setChartDraft({
                                id: chart.id, name: chart.name, chart_type: chart.chart_type,
                                ...chart.config,
                              })}>
                                <EditOutlined sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove chart">
                              <IconButton size="small" onClick={() => deleteChart(chart.id)}>
                                <DeleteOutlineRounded sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                      <ReportChart result={result} config={chart.config}
                                   chartType={chart.chart_type} currency={currency} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </MDBox>

      <Menu anchorEl={exportMenu} open={Boolean(exportMenu)} onClose={() => setExportMenu(null)}>
        <MenuItem onClick={() => exportAs('csv')}>CSV</MenuItem>
        <MenuItem onClick={() => exportAs('xlsx')}>Excel</MenuItem>
        <MenuItem onClick={() => exportAs('pdf')}>PDF</MenuItem>
      </Menu>

      <ShareDialog open={sharing} onClose={() => setSharing(false)}
                   resourceType="report" resource={report} />

      <ScheduleDialog open={scheduling} onClose={() => setScheduling(false)}
                      report={report} />

      {/* --------------------------------------------------- chart editor */}
      <Dialog open={Boolean(chartDraft)} onClose={() => setChartDraft(null)}
              fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {chartDraft?.id ? 'Edit chart' : 'Add a chart'}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
            <TextField
              size="small" fullWidth label="Chart name" value={chartDraft?.name ?? ''}
              onChange={(e) => setChartDraft((d) => ({ ...d, name: e.target.value }))}
            />
            <Box display="flex" gap={1}>
              <TextField
                select size="small" fullWidth label="Type"
                value={chartDraft?.chart_type ?? 'bar'}
                onChange={(e) => setChartDraft((d) => ({ ...d, chart_type: e.target.value }))}
              >
                {CHART_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                select size="small" fullWidth label="Group (category)"
                value={chartDraft?.category_key ?? ''}
                onChange={(e) => setChartDraft((d) => ({ ...d, category_key: e.target.value }))}
              >
                {(result?.columns ?? []).filter((c) => !c.is_aggregate).map((c) => (
                  <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" fullWidth label="Measure (value)"
                value={chartDraft?.value_key ?? ''}
                onChange={(e) => setChartDraft((d) => ({ ...d, value_key: e.target.value }))}
              >
                {(result?.columns ?? []).filter((c) => c.is_aggregate).map((c) => (
                  <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Divider />
            <Typography variant="caption" color="text.secondary">Preview</Typography>
            <ReportChart
              result={result}
              config={{ category_key: chartDraft?.category_key,
                        value_key: chartDraft?.value_key }}
              chartType={chartDraft?.chart_type} currency={currency} height={220}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setChartDraft(null)}>Cancel</MDButton>
          <MDButton
            variant="gradient" color="info" onClick={saveChart}
            disabled={!chartDraft?.name?.trim() || !chartDraft?.category_key
                      || !chartDraft?.value_key}
          >
            Save chart
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(drill)} onClose={() => setDrill(null)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Records behind {drill?.label || 'this row'}
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {drill?.loading
              ? 'Loading…'
              : drillRecordSummary(drill)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {drill?.loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : drill?.error ? (
            <Alert severity="warning" sx={{ m: 2 }}>{drill.error}</Alert>
          ) : (
            <ReportTable result={drill?.result} currency={currency} dense
                         maxHeight="70vh" showRowCount />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setDrill(null)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

function iso(date) {
  return date.toISOString().slice(0, 10);
}

/** A cell rendered for a dialog title, without pulling in the full formatter. */
function formatCell(value, column) {
  if (value === null || value === undefined || value === '') return 'Blank';
  if (column?.type === 'date' || column?.bucket) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    }
  }
  return String(value);
}

/**
 * Columns for the drilled record list.
 *
 * A grouped report has no column list of its own, so one is composed: a
 * name-ish field to identify the record, the measures the report summarised,
 * and a date to place it in time.
 *
 * Every candidate is checked against the catalog before being used. The
 * engine validates field names and returns 422 for an unknown one, so an
 * unchecked wish-list of likely names ("subject", "order_number") makes
 * drill-down fail on every object that happens not to have one of them.
 */
function drillColumns(fields, dateFields, report) {
  const available = new Set((fields ?? []).map((f) => f.api_name));

  const chosen = [];
  const push = (field) => {
    if (field && available.has(field) && !chosen.includes(field)) {
      chosen.push(field);
    }
  };

  // id first so each row is uniquely identifiable when names collide, and so
  // the visible row count cannot be mistaken for a de-duplicated list.
  push('id');

  ['name', 'title', 'subject', 'first_name', 'last_name', 'company_name',
   'order_number', 'invoice_number', 'case_number'].forEach(push);

  (report.definition?.aggregates ?? [])
    .map((a) => a.field)
    .filter((f) => f && f !== 'id')
    .forEach(push);

  (report.definition?.group_by ?? []).forEach((g) => push(g.field));
  push(dateFields?.[0]?.api_name);

  // The engine refuses a tabular report with no columns, and an empty drill
  // dialog is worse than a bare list of ids.
  if (!chosen.length) push('id');

  return chosen.slice(0, 8).map((field) => ({ field }));
}

/** Subtitle for the drill dialog: always state how many rows came back. */
function drillRecordSummary(drill) {
  if (drill?.error) {
    return 'The report\'s own filters still apply, narrowed to this group.';
  }
  const count = drill?.result?.row_count;
  if (typeof count !== 'number') {
    return 'The report\'s own filters still apply, narrowed to this group.';
  }
  const loaded = `${count.toLocaleString()} record${count === 1 ? '' : 's'}`;
  const expected = drill?.expectedCount;
  if (
    expected != null
    && Number.isFinite(expected)
    && !drill?.result?.truncated
    && count !== expected
  ) {
    return `${loaded} loaded — summary showed ${expected.toLocaleString()}. Scroll the list; counts should match.`;
  }
  if (drill?.result?.truncated) {
    return `Showing the first ${count.toLocaleString()} of more than ${drill.result.limit.toLocaleString()} matching records. Scroll for all loaded rows.`;
  }
  if (expected != null && Number.isFinite(expected)) {
    return `${loaded} — matches the summary count of ${expected.toLocaleString()}. Scroll if the list is taller than the dialog.`;
  }
  return `${loaded}. Scroll if the list is taller than the dialog.`;
}

function newChartDraft(result, order) {
  const category = result?.columns?.find((c) => !c.is_aggregate);
  const value = result?.columns?.find((c) => c.is_aggregate);
  return {
    name: value && category ? `${value.label} by ${category.label}` : 'New chart',
    chart_type: 'bar',
    category_key: category?.key ?? '',
    value_key: value?.key ?? '',
    order_index: order,
  };
}

function filenameFrom(response, reportName, format) {
  const header = response?.headers?.['content-disposition'];
  const match = header && /filename="?([^";]+)"?/.exec(header);
  if (match) return match[1];
  return `${(reportName || 'report').replace(/[^\w -]/g, '_')}.${format}`;
}

async function readBlobError(err) {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return readError(err, null);
  try {
    const parsed = JSON.parse(await data.text());
    const detail = parsed?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
  } catch {
    // Not JSON; fall through to the generic message.
  }
  return null;
}

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}
